import { useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import i18n from '../i18n';
import { getUserLabels } from '../services/userService';
import { appEventEmitter, FETCH_ERROR_EVENT } from '../services/eventEmitter';

/**
 * Fetches per-user translation labels once the user has been granted
 * access, and merges them into i18n. Callers pass `enabled` to gate the
 * fetch - we only want to load labels for users who are actually going
 * to enter the app, not for users who are signed in but lack portal
 * roles (those would hit an endpoint that 500s on unknown users).
 *
 * Runs on both entry paths:
 *   - fresh login (MsalProvider re-renders after LOGIN_SUCCESS)
 *   - refresh with an existing session (index.tsx restores the account
 *     before first render)
 *
 * Guards against double-fetching per homeAccountId so StrictMode's
 * intentional double-mount or any transient re-render does not trigger
 * redundant network calls.
 */
export const useUserLabels = (enabled: boolean) => {
  const { accounts, instance } = useMsal();
  const loadedForAccountId = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      loadedForAccountId.current = null;
      return;
    }
    if (loadedForAccountId.current === account.homeAccountId) return;
    loadedForAccountId.current = account.homeAccountId;

    const username = account.idTokenClaims?.preferred_username as
      | string
      | undefined;
    if (!username) return;

    const abortController = new AbortController();
    getUserLabels(abortController, username)
      .then((labels) => {
        if (!labels) return;
        const langKey = 'clientX';
        i18n.addResourceBundle(langKey, 'translation', labels, true, true);
        return i18n.changeLanguage(langKey);
      })
      .catch((error) => {
        appEventEmitter.emit(FETCH_ERROR_EVENT, `API Error: ${error}`);
      });

    return () => {
      abortController.abort();
    };
  }, [accounts, instance, enabled]);
};
